"use client";

import * as React from "react";
import { Expand, Maximize2 } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
const carouselData = [{
  title: "Last Mile Efficiency in DFW",
  subtitle: "Precision delivery for stone and lumber yards across North Texas with our specialized fleet.",
  img: "https://harmless-hippo.10web.cloud/wp-content/uploads/2026/04/Image-3.jpeg?v=t29fro2iqw"
}, {
  title: "Regional Heavy Haulage",
  subtitle: "Reliable semi-truck transport servicing the Southeast region with industrial-grade reliability.",
  img: "https://harmless-hippo.10web.cloud/wp-content/uploads/2026/04/Image-2.jpeg?v=6p6vbg2s70j"
}, {
  title: "Bulk Material Logistics",
  subtitle: "Expert handling and transport for lumber, aggregate, and large-scale building materials.",
  img: "https://harmless-hippo.10web.cloud/wp-content/uploads/2026/04/Image-4.jpeg?v=37jkrsv0ynk"
}];
const gridData = [{
  title: "DFW Delivery Fleet",
  desc: "Small and medium vehicles optimized for tight urban sites and residential last-mile delivery.",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/gallery_grid_fleet.png"
}, {
  title: "Southeast Semi Transport",
  desc: "Heavy-duty trucks equipped for long-distance hauling across the entire Southeast market.",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/gallery_grid_semi.png"
}, {
  title: "Industrial Material Handling",
  desc: "Specialized equipment for the safe loading and unloading of heavy stone and construction lumber.",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/gallery_grid_materials.png"
}, {
  title: "Real-Time Logistics Tracking",
  desc: "Advanced dispatch and monitoring systems ensuring your bulk materials arrive exactly when needed.",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/gallery_grid_tracking.png"
}, {
  title: "Lumber Yard Solutions",
  desc: "Dedicated logistics partnerships for lumber yards requiring high-frequency, reliable transport.",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/gallery_grid_lumber.png"
}, {
  title: "Stone & Aggregate Logistics",
  desc: "Robust transport solutions for heavy materials, from landscaping stone to structural masonry.",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/gallery_grid_stone.png"
}];
export default function Gallery() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="189147" id="galleryslider" className="bg-background py-24 overflow-hidden text-foreground" ref={ref}>
      <div className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          <Badge variant="outline" className="px-4 py-1 text-sm uppercase tracking-wider border-secondary text-secondary-foreground bg-primary font-default font-bold rounded-sm">
            Operations Gallery
          </Badge>
          <h2 className="text-4xl md:text-6xl font-bold text-secondary tracking-tighter max-w-3xl font-default">Legacy Delivery Logistics in Action</h2>
          <p className="text-muted-foreground text-lg max-w-2xl font-default">Explore our DFW delivery network and regional heavy haulage operations servicing stone companies, retail stores, lumber yards and more.</p>
        </div>

        {}
        <div className="mb-24 relative px-12 md:px-16">
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent>
              {carouselData.map((item, i) => <CarouselItem key={i} data-index={i}>
                  <div className="relative overflow-hidden rounded-sm border-2 border-secondary shadow-md group">
                    <AspectRatio ratio={21 / 9} className="bg-muted">
                      <img src={item.img} alt={item.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" />
                      {}
                      <div className="absolute inset-0 bg-gradient-to-t from-secondary/90 via-secondary/40 to-transparent" />
                      
                      {}
                      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 flex flex-col justify-end h-full">
                        <h3 className="text-2xl md:text-4xl font-bold text-white mb-2 drop-shadow-md font-default">
                          {item.title}
                        </h3>
                        <p className="text-white/90 text-sm md:text-lg max-w-3xl drop-shadow-sm font-default">
                          {item.subtitle}
                        </p>
                      </div>
                    </AspectRatio>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <CarouselPrevious className="left-0 md:left-4 size-10 bg-primary text-primary-foreground border-secondary hover:bg-primary/90 rounded-sm shadow-md" />
            <CarouselNext className="right-0 md:right-4 size-10 bg-primary text-primary-foreground border-secondary hover:bg-primary/90 rounded-sm shadow-md" />
          </Carousel>
        </div>

        {}
        <div className="space-y-8">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-secondary font-default uppercase tracking-tight">Fleet & Infrastructure</h3>
            <div className="h-0.5 flex-1 bg-secondary ml-6 hidden md:block" />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {gridData.map((item, i) => <div key={i} data-index={i} className="h-full">
                <Dialog>
                  <DialogTrigger asChild>
                    <Card className="h-full cursor-pointer group overflow-hidden border-2 border-secondary bg-card transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col rounded-sm">
                      <CardContent className="p-0 flex-1 flex flex-col">
                        <div className="relative overflow-hidden">
                          <AspectRatio ratio={4 / 3}>
                            <img src={item.img} alt={item.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110" />
                          </AspectRatio>
                          <div className="absolute inset-0 bg-secondary/0 group-hover:bg-secondary/40 transition-colors duration-300 flex items-center justify-center">
                            <div className="bg-primary text-primary-foreground p-3 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-4 group-hover:translate-y-0 shadow-md">
                              <Maximize2 className="size-5" />
                            </div>
                          </div>
                        </div>
                        <div className="p-6 flex-1 flex flex-col justify-center border-t-2 border-secondary">
                          <h4 className="font-bold text-lg text-secondary mb-2 line-clamp-1 font-default uppercase">
                            {item.title}
                          </h4>
                          <p className="text-sm text-muted-foreground line-clamp-2 font-default">
                            {item.desc}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-4xl p-0 overflow-hidden border-2 border-secondary bg-background shadow-2xl rounded-sm">
                    <div className="relative">
                      <img src={item.img} alt={item.title} className="w-full h-auto max-h-[70vh] object-contain bg-muted" />
                    </div>
                    <DialogHeader className="p-6 bg-card border-t-2 border-secondary">
                      <DialogTitle className="text-2xl text-secondary font-default font-bold uppercase">{item.title}</DialogTitle>
                      <DialogDescription className="text-base text-muted-foreground mt-2 font-default">
                        {item.desc}
                      </DialogDescription>
                    </DialogHeader>
                  </DialogContent>
                </Dialog>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}